import { Component, Input, inject } from '@angular/core';
import { LoadingService } from './loading.service';

/**
 * Componente de loading reutilizável.
 *
 * ── Modos de exibição ─────────────────────────────────────────────────────────
 *
 * 1. Overlay fixo (cobre tela toda com backdrop escuro):
 * ```html
 * <app-loading [visible]="isLoading" />
 * <app-loading [visible]="isLoading" message="Salvando..." />
 * ```
 *
 * 2. Overlay inline (sobre o elemento pai com `position: relative`):
 * ```html
 * <app-loading [visible]="isLoading" [inline]="true" />
 * ```
 *
 * 3. Centralizado na página (sem backdrop, preenche altura disponível):
 * ```html
 * <app-loading [visible]="isLoading" [page]="true" />
 * <app-loading [visible]="isLoading" [page]="true" message="Carregando dados..." />
 * ```
 *
 * 4. Global reativo ao LoadingService (sem @Input — coloque em app.html):
 * ```html
 * <app-loading />
 * ```
 *
 * ── Uso via serviço (TypeScript) ──────────────────────────────────────────────
 * ```ts
 * private loading = inject(LoadingService);
 *
 * this.loading.show('Salvando...');
 * // ... operação assíncrona ...
 * this.loading.hide();
 * ```
 */
@Component({
  selector: 'app-loading',
  standalone: true,
  template: `
    @if (isVisible) {
      <div class="ld-overlay"
           [class.ld-overlay--inline]="inline"
           [class.ld-overlay--page]="page"
           role="status"
           aria-live="polite">
        <div class="ld-box" [class.ld-box--page]="page">
          <div class="ld-spinner" [class.ld-spinner--page]="page" aria-hidden="true">
            <div class="ld-spinner__ring"></div>
          </div>
          @if (currentMessage) {
            <p class="ld-msg" [class.ld-msg--page]="page">{{ currentMessage }}</p>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    /* ── Base overlay (fixed, dark backdrop) ── */
    .ld-overlay {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(15, 23, 42, 0.45);
      backdrop-filter: blur(2px);
      animation: ld-fade-in .15s ease;
    }

    /* ── Inline: absolute over parent ── */
    .ld-overlay--inline {
      position: absolute;
      border-radius: inherit;
    }

    /* ── Page: centered in content area, no backdrop ── */
    .ld-overlay--page {
      position: static;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 200px;
      width: 100%;
      background: transparent;
      backdrop-filter: none;
    }

    /* ── Box ── */
    .ld-box {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
    }

    .ld-box--page {
      gap: 16px;
    }

    /* ── Spinner ── */
    .ld-spinner {
      width: 44px;
      height: 44px;
      position: relative;
    }

    .ld-spinner--page {
      width: 48px;
      height: 48px;
    }

    .ld-spinner__ring {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      border: 3px solid rgba(255, 255, 255, 0.25);
      border-top-color: #fff;
      animation: ld-spin .7s linear infinite;
    }

    .ld-overlay--page .ld-spinner__ring {
      border-color: #e2e8f0;
      border-top-color: #3b82f6;
    }

    /* ── Message ── */
    .ld-msg {
      margin: 0;
      font-size: 13px;
      font-weight: 500;
      color: #fff;
      letter-spacing: 0.01em;
    }

    .ld-msg--page {
      color: #64748b;
      font-size: 14px;
    }

    /* ── Animations ── */
    @keyframes ld-spin {
      to { transform: rotate(360deg); }
    }

    @keyframes ld-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
  `],
})
export class LoadingComponent {
  /**
   * Força visibilidade do overlay diretamente no template.
   * Quando `null` (padrão), o estado é lido do `LoadingService`.
   */
  @Input() visible: boolean | null = null;

  /** Mensagem exibida abaixo do spinner. Quando `null`, usa a do `LoadingService`. */
  @Input() message: string | null = null;

  /**
   * Modo inline: overlay absoluto sobre o elemento pai (`position: relative`).
   * Não pode ser combinado com `[page]`.
   */
  @Input() inline = false;

  /**
   * Modo página: spinner azul centralizado sem backdrop, preenche a altura disponível.
   * Ideal para loading de página ou seção antes do conteúdo renderizar.
   * Não pode ser combinado com `[inline]`.
   */
  @Input() page = false;

  private readonly loadingService = inject(LoadingService);

  get isVisible(): boolean {
    return this.visible !== null ? this.visible : this.loadingService.visible();
  }

  get currentMessage(): string {
    return this.message !== null ? this.message : this.loadingService.message();
  }
}
